import { styled } from '@mui/material';
import { useNotInViewCount } from 'hooks/useNotInViewCount';
import { Chip, Tooltip } from 'ui/components';
import { Fragment } from 'react';

const CellContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '5px',
  width: '100%'
});

const ChipsContainer = styled('div', {
  shouldForwardProp: prop => !['shouldNotAddGap', 'shouldDisplayFirstValue'].includes(prop as string)
})<{ shouldNotAddGap: boolean; shouldDisplayFirstValue: boolean }>(({ shouldNotAddGap, shouldDisplayFirstValue }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  maxHeight: `24px`,
  overflow: 'hidden',
  ...(shouldDisplayFirstValue ? { width: 'min-content' } : {}),
  ...(shouldNotAddGap ? {} : { gap: '5px' })
}));

const MoreChipsContainer = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px'
}));

type ChipCellProps = {
  value?: string[];
  isChipsBackgroundHidden?: boolean;
  isSeperatedByComma?: boolean;
  isDefaultFont?: boolean;
  shouldDisplayFirstValue?: boolean;
};

export const ChipsCell = ({
  value,
  isChipsBackgroundHidden = false,
  isSeperatedByComma = false,
  isDefaultFont = false,
  shouldDisplayFirstValue = false
}: ChipCellProps) => {
  const { notInViewCount, containerRef } = useNotInViewCount();

  if (!value) {
    return null;
  }

  const styleOverride = isChipsBackgroundHidden
    ? { backgroundColor: 'unset', '& > .MuiChip-label': { padding: 0, paddingInlineEnd: '5px' } }
    : {
        borderRadius: '25px',
        padding: '7px 10px'
      };

  return (
    <CellContainer>
      <ChipsContainer
        ref={containerRef}
        shouldNotAddGap={isSeperatedByComma}
        shouldDisplayFirstValue={shouldDisplayFirstValue}
      >
        {value.map((name, index) => {
          const shouldAddComma =
            isSeperatedByComma && index !== value.length - 1 && index !== value.length - 1 - notInViewCount;
          const label = shouldAddComma ? `${name},` : name;
          return (
            <Fragment key={index + 'displayed-chip-container'}>
              <Chip isDefaultFont={isDefaultFont} sx={styleOverride} size="small" label={label} />
            </Fragment>
          );
        })}
      </ChipsContainer>
      {notInViewCount > 0 && (
        <Tooltip
          title={
            <MoreChipsContainer>
              {value.slice(-notInViewCount).map((name, index) => {
                return (
                  <Chip
                    isDefaultFont={isDefaultFont}
                    style={styleOverride}
                    size="small"
                    key={index + 'hidden-chip-container'}
                    label={name}
                  />
                );
              })}
            </MoreChipsContainer>
          }
        >
          <Chip
            sx={{
              borderRadius: '25px',
              padding: '7px 10px',
              '& > .MuiChip-label': { padding: 0 }
            }}
            isDefaultFont={isDefaultFont}
            size="small"
            label={`+${notInViewCount}`}
          />
        </Tooltip>
      )}
    </CellContainer>
  );
};
